import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';

import MarkerActionBar from './MarkerActionBar';
import MapPopup, { MapPopupRef } from '../MapPopup';

import { useSelector, useDispatch } from 'react-redux';
import {
  updateLayerByLayerId,
} from 'features/advancedMapState';
import 'assets/styles/MarkerTable.scss';
import { RootState } from 'types/types';

interface Marker {
  VENDOR: string;
  SITE_NAME: string;
  ECI: string;
  OPERATIONAL_STATE: string;
  ADMINISTRATIVE_STATE: string;
  REGION_NAME: string;
  CITY_NAME: string;
  AREA_NAME: string;
  CELL_CLASS: string;
  DL_BANDWIDTH: string;
  CELL_Name: string;
  CELL_BAND_Name: string;
  CELL_CATEGORY: string;
  CELL_DEPLOYMENT_STATUS: string;
  site: string;
  name: string;
  [key: string]: string;
}

interface KpiProperties {
  id: string;
  name: string;
}

interface MarkerTableProps {
  markers: Marker[];
  cellDetailsOpen: boolean;
  mapPopupStateVisibility: boolean;
  kpiNRPropertyName: KpiProperties;
  filteredMarkersWithKPI: Marker[];
  kpiLTEPropertyName: KpiProperties;
  handleKPILayerDataEvent: () => void;
  handleCloseMarkersModal: () => void;
  kqiFiltersSelected: React.MutableRefObject<{ id: string; name: string }[]>;
}

export interface MarkerTableRef {
  mapPopupRef: React.RefObject<MapPopupRef>;
}

const MarkerTable: React.ForwardRefRenderFunction<
  MarkerTableRef,
  MarkerTableProps
> = (
  {
    markers,
    cellDetailsOpen,
    kpiNRPropertyName,
    kpiLTEPropertyName,
    kqiFiltersSelected,
    filteredMarkersWithKPI,
    handleKPILayerDataEvent,
    handleCloseMarkersModal,
    mapPopupStateVisibility,
  },
  ref
) => {
  const dispatch = useDispatch();
  const {
    globalLayers: globalLayers,
  } = useSelector((state: RootState) => state.advancedMap);
  const kpiLayer = globalLayers.find((el) => el.LayerName == 'Technology KPI');
  const mapPopupRef = useRef<MapPopupRef>(null);

  const [markersData, setMarkersData] = useState<Marker[]>(markers);

  const sortedMarkers = markersData.sort((a, b) => {
    const bandA = a['CELL BAND'];
    const bandB = b['CELL BAND'];
    if (bandA < bandB) return -1;
    if (bandA > bandB) return 1;
    return 0;
  });

  const [kpiLayerSelected, setKpiLayerSelected] = useState<boolean>(
    (kpiLayer && kpiLayer?.selected) ? kpiLayer.selected : false
  );

  useEffect(() => {
    setKpiLayerSelected((kpiLayer && kpiLayer?.selected) ? kpiLayer.selected : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLayers]);

  const handleLoadKPIValues = () => {
    handleKPILayerDataEvent();

    if (kpiLayer)
      dispatch(
        updateLayerByLayerId({
          LayerId: kpiLayer.LayerId,
          diff: {
            selected: true
          }
        })
      );
    setKpiLayerSelected(true);
  };

  useEffect(() => {
    const markersUpdated = markers.map((obj2) => {
      const matchingObj = filteredMarkersWithKPI.find(
        (obj1) => obj1.id === obj2.id
      );
      return matchingObj ? { ...obj2, ...matchingObj } : obj2;
    });
    setMarkersData(markersUpdated);
  }, [filteredMarkersWithKPI, markers]);

  useImperativeHandle(ref, () => ({
    mapPopupRef: mapPopupRef
  }));

  return (
    <MapPopup
      ref={mapPopupRef}
      cellDetailsOpen={cellDetailsOpen}
      handleClose={handleCloseMarkersModal}
      mapPopupStateVisibility={mapPopupStateVisibility}
      title={`Highlighted Cells Info (${markers.length} cells)`}
    >
      <TableContainer className="marker-table">
        <Box sx={{ maxHeight: '100%', overflowY: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Vendor Name</TableCell>
                <TableCell>Site Name</TableCell>
                <TableCell>Cell Name</TableCell>
                <TableCell>DL Bandwidth</TableCell>
                <TableCell>UL Bandwidth</TableCell>
                <TableCell>Cell Band Name</TableCell>
                <TableCell>Deployment Status</TableCell>
                <TableCell>Region Name</TableCell>
                <TableCell>City Name</TableCell>
                <TableCell>Area Name</TableCell>
                <TableCell>Cell Class</TableCell>
                <TableCell>Cell Category</TableCell>
                <TableCell>Site Type</TableCell>
                {kpiLayerSelected && (
                  <>
                    <TableCell>{kpiNRPropertyName?.name}</TableCell>
                    <TableCell>{kpiLTEPropertyName?.name}</TableCell>
                    {kqiFiltersSelected.current.map((el, index) => (
                      <TableCell key={`${el}-${index}`}>{el.name}</TableCell>
                    ))}
                    <TableCell>Cell Trouble Tickets Count</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedMarkers.map((marker, mIndex) => (
                <TableRow key={mIndex}>
                  <TableCell>{marker['VENDOR']}</TableCell>
                  <TableCell>{marker['SITE NAME']}</TableCell>
                  <TableCell>{marker['CELL Name']}</TableCell>
                  <TableCell>{marker['DL BANDWIDTH']}</TableCell>
                  <TableCell>{marker['UL BANDWIDTH']}</TableCell>
                  <TableCell>{marker['CELL BAND Name']}</TableCell>
                  <TableCell>{marker['DEPLOYMENT STATUS']}</TableCell>
                  <TableCell>{marker['REGION']}</TableCell>
                  <TableCell>{marker['CITY']}</TableCell>
                  <TableCell>{marker['AREA']}</TableCell>
                  <TableCell>{marker['CELL CLASS']}</TableCell>
                  <TableCell>{marker['CELL CATEGORY']}</TableCell>
                  <TableCell>{marker['SITE TYPE']}</TableCell>
                  {kpiLayerSelected && (
                    <>
                      <TableCell className="kpi-cell">
                        {marker[kpiNRPropertyName.id] === undefined
                          ? 'N/A'
                          : marker[kpiNRPropertyName.id]}
                      </TableCell>
                      <TableCell className="kpi-cell">
                        {marker[kpiLTEPropertyName.id] === undefined
                          ? 'N/A'
                          : marker[kpiLTEPropertyName.id]}
                      </TableCell>
                      {kqiFiltersSelected.current.map((el, index) => (
                        <TableCell key={`${el}-${index}`} className="kpi-cell">
                          {marker[el.id] === undefined ? 'N/A' : marker[el.id]}
                        </TableCell>
                      ))}
                      <TableCell className="kpi-cell">
                        {marker['CELL TROUBLE TICKETS COUNT'] === undefined
                          ? 'N/A'
                          : marker['CELL TROUBLE TICKETS COUNT']}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
      {!mapPopupStateVisibility && (
        <MarkerActionBar
          kpiLayerSelected={kpiLayerSelected}
          handleLoadKPIValues={handleLoadKPIValues}
        />
      )}
    </MapPopup>
  );
};

export default forwardRef<MarkerTableRef, MarkerTableProps>(MarkerTable);
